import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import es, { type Dict } from "./es";
import en from "./en";

export type Locale = "es" | "en";

const dicts: Record<Locale, Dict> = { es, en };

type I18nValue = {
  locale: Locale;
  t: (key: keyof Dict, vars?: Record<string, string | number>) => string;
  setLocale: (locale: Locale) => void;
};

const I18nContext = createContext<I18nValue | null>(null);

const STORAGE_KEY = "pahcertificar.app.locale";

function detect(): Locale {
  if (typeof window === "undefined") return "es";
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved === "en" || saved === "es") return saved;
  const nav = window.navigator.language.toLowerCase();
  return nav.startsWith("en") ? "en" : "es";
}

function interpolate(s: string, vars?: Record<string, string | number>) {
  if (!vars) return s;
  return s.replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : `{${k}}`));
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<Locale>(detect);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, locale);
    document.documentElement.lang = locale === "es" ? "es-CO" : "en";
  }, [locale]);

  const value = useMemo<I18nValue>(() => {
    const dict = dicts[locale];
    return {
      locale,
      setLocale,
      t: (key, vars) => interpolate(dict[key] ?? es[key] ?? String(key), vars),
    };
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useT must be used inside I18nProvider");
  return ctx;
}
